import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type { ProfessionalDetails } from '@/types/settings-api';

/**
 * Practitioner Professional Details — PROD-761 commit 3.
 *
 * GET  /api/ProfessionalDetails/{doctorSetupId}  — fetch
 * POST /api/ProfessionalDetails                  — save
 *
 * Body shape verified against working dev cURLs 2026-05-13. Wire-shape
 * notes (do NOT regress):
 * - British spelling: `licenceAuthorityId` / `licenceNo` on the wire.
 *   Field names verbatim; display labels can use American spelling.
 * - `expirationDate` is no-Z ISO (.NET model-binder rule).
 * - Nested `code` objects on languages / speciality / skills carry full
 *   SNOMED structure including `description: null` and `shortName: null`
 *   defaults when ommited.
 *
 * Save is symmetric — POST body has the same shape as GET response,
 * plus an explicit `doctorSignatureUrl` field that the GET may omit.
 *
 * Signature URL round-trip (intentional, not deferred):
 *   The `doctorSignatureUrl` field stays on the wire (GET → React state →
 *   POST) untouched. NO upload UI is rendered on the Professional tab —
 *   Flutter commented out the `DoctorSignatureWidget` at
 *   doctor_professional_tab.dart:81 before this rewrite started, so
 *   Scrips effectively retired the feature in the deployed app. If the
 *   feature is resurrected (Flutter re-enables the widget OR a fresh
 *   product spec lands), wire an upload mutation here and surface a
 *   control on the Professional tab. Until then, the field passes
 *   through silently and any existing value on the record is preserved.
 *   See practitioner-professional-tab.tsx header doc for the UI side.
 */
export function useProfessionalDetails(
  doctorSetupId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['settings', 'professional-details', doctorSetupId],
    queryFn: ({ signal }) => {
      if (!doctorSetupId) {
        throw new Error('useProfessionalDetails called without doctorSetupId');
      }
      return get<ProfessionalDetails>(
        `/api/ProfessionalDetails/${doctorSetupId}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId,
  });
}

export function useSaveProfessionalDetails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (details: ProfessionalDetails) =>
      post<ProfessionalDetails>('/api/ProfessionalDetails', details),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'professional-details', vars.doctorSetupId],
      });
    },
  });
}
